/*
 * ПАНЕЛЬ УРОКА — боковая колонка справа от сцены.
 *
 * Здесь живёт весь учебный слой: теория, шаги практики, задание и кнопка
 * «Проверить». Ребёнок не уходит из редактора — посмотрел, что надо сделать,
 * сделал в той же вкладке, нажал «Проверить», получил разбор.
 *
 * ВАЖНО ПРО РАЗМЕЩЕНИЕ. Панель ставится в разметке KubikonCAD рядом с
 * .viewportPane — то есть СНАРУЖИ слоёв канваса. Внутрь <Canvas> её класть
 * нельзя: всё, что рисует DOM через <Html> из drei, ломает порядок вставки
 * узлов и роняет редактор (см. комментарий в KubikonCAD.jsx).
 *
 * ПОДСКАЗКИ ОТКРЫВАЮТСЯ ПО ОДНОЙ. Три уровня: намёк → наводящий вопрос →
 * разбор шага. Ребёнок сам решает, насколько глубоко нырять; сразу вываливать
 * готовое решение — значит лишить его задачи.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useCadStore from '../store/useCadStore';
import { getGeometryClient } from '../geometryClient';
import { getCurrentUserId } from '../cloudIO';
import { checkCriteria, criterionLabel } from './checker';
import { findLesson, lessonIndex } from './courses';
import { lessonProgress, toggleStep, recordAttempt } from './progress';
import Blueprint from './Blueprint';
import TheaterModal from './TheaterModal';
import cl from './LessonPanel.module.css';


/** Галочка в кружке — состояние шага или критерия. */
const CheckIcon = ({ done }) => (
    <svg className={done ? cl.checkOn : cl.checkOff} viewBox="0 0 20 20" width="18" height="18">
        <circle cx="10" cy="10" r="8.5" fill="none" strokeWidth="1.5"/>
        {done && <path d="M6 10.5l2.7 2.7L14.2 7.7" fill="none" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round"/>}
    </svg>
);

/** Крестик — критерий не выполнен. */
const CrossIcon = () => (
    <svg className={cl.crossIcon} viewBox="0 0 20 20" width="18" height="18">
        <circle cx="10" cy="10" r="8.5" fill="none" strokeWidth="1.5"/>
        <path d="M7 7l6 6M13 7l-6 6" fill="none" strokeWidth="2" strokeLinecap="round"/>
    </svg>
);


/**
 * Собрать измерения сцены для проверки.
 *
 * Меряем каждое тело документа через ядро (MEASURE_BODY) и добавляем список
 * видов операций из документа — он нужен критерию «feature».
 */
async function measureScene() {
    const state = useCadStore.getState();
    const client = getGeometryClient();

    /*
     * ⚠️ ТЕЛА БЕРЁМ ИЗ bodyMeshes, А НЕ ИЗ doc.bodies.
     *
     * doc.bodies — поле схемы документа, в котором лежат МЕТАДАННЫЕ тел, и
     * его единственный сеттер (setBodies) в коде редактора не вызывается
     * ни разу: массив всегда пустой. Реальные тела живут в bodyMeshes —
     * это Map, которую наполняет ядро по мере построения (см. geometryClient,
     * сообщения BODY_MESH / BODY_REMOVED).
     *
     * Из-за этой путаницы проверка на живой сцене докладывала «тел 0, а нужно 1»
     * при явно построенной детали.
     */
    const bodyIds = Array.from(state.bodyMeshes.keys());

    const bodies = [];
    for (const bodyId of bodyIds) {
        try {
            const m = await client.measureBody(bodyId);
            bodies.push({
                id: bodyId,
                volume: m.volume,
                area: m.area,
                com: m.com,
                bbox: m.bbox,
                holeCount: m.holeCount,
            });
        } catch (_) { /* тело не измерилось — пропускаем, критерии это заметят */ }
    }

    // Сортируем по объёму убыванием: критерии вида size/volume смотрят на
    // первое тело, и это должна быть основная деталь, а не мелкая крышка.
    bodies.sort((a, b) => (Number(b.volume) || 0) - (Number(a.volume) || 0));

    // Отверстия суммируем по всем телам. Если ни одно тело не отдало счётчик
    // (старый воркер в кэше браузера) — оставляем null: критерий «отверстия»
    // тогда не проверяется, вместо ложного «неверно».
    const counted = bodies.filter((b) => b.holeCount != null);
    const holeCount = counted.length
        ? counted.reduce((sum, b) => sum + b.holeCount, 0)
        : null;

    const featureKinds = (state.doc.features || [])
        .filter((f) => !f.suppressed)
        .map((f) => f.kind);

    return { bodies, holeCount, featureKinds };
}


const LessonPanel = ({ lessonId, onClose }) => {
    const found = findLesson(lessonId);
    const userId = getCurrentUserId();
    const navigate = useNavigate();

    const [progress, setProgress] = useState(() => lessonProgress(userId, lessonId));
    const [checking, setChecking] = useState(false);
    const [result, setResult] = useState(null);      // { passed, results }
    const [hintLevel, setHintLevel] = useState(0);   // сколько подсказок открыто
    const [blueprintOpen, setBlueprintOpen] = useState(false);  // чертёж крупно

    /*
     * Теория показывается мультиком в модалке при входе в урок.
     *
     * Раньше она лежала текстом во вкладке этой панели — и не работала:
     * дети не читают. На первой версии школы это уже проверяли на хороших
     * подробных уроках, их не читал никто. Поэтому теперь урок открывается
     * с мультика: диктор проговаривает, ребёнок смотрит, потом само
     * открывается задание.
     *
     * Повторно мультик не навязываем: если урок уже пройден, сразу задание,
     * а пересмотреть можно кнопкой «Теория» в шапке панели.
     */
    const [theaterOpen, setTheaterOpen] = useState(
        () => !lessonProgress(userId, lessonId).done,
    );

    // Смена урока — сбрасываем всё, что относилось к прошлому.
    useEffect(() => {
        const p = lessonProgress(userId, lessonId);
        setProgress(p);
        setResult(null);
        setHintLevel(0);
        setTheaterOpen(!p.done);
    }, [lessonId, userId]);

    const runCheck = useCallback(async () => {
        if (!found) return;
        setChecking(true);
        setResult(null);
        try {
            const measured = await measureScene();
            const outcome = checkCriteria(found.lesson.criteria, measured);
            /*
             * Диагностика непройденной проверки. Когда критерий «нужна такая-то
             * операция» не сходится, из сообщения не видно, ЧТО именно видит
             * проверка: операции нет в истории, или она называется иначе.
             * Печатаем фактический список — по нему причина видна сразу.
             */
            if (!outcome.passed) {
                console.info('[урок] операции в документе:',
                    measured.featureKinds.length
                        ? measured.featureKinds.join(', ')
                        : '(ни одной)');
                console.info('[урок] тел:', measured.bodies.length,
                    '| отверстий:', measured.holeCount);
            }
            /* Список операций кладём в результат: панель показывает его при
               незачёте — видно, что именно проверка нашла в документе. */
            setResult({ ...outcome, featureKinds: measured.featureKinds });
            setProgress(recordAttempt(userId, lessonId, outcome.passed));
        } catch (e) {
            setResult({
                passed: false,
                results: [],
                error: 'Не получилось измерить деталь: ' + (e?.message || e),
            });
        } finally {
            setChecking(false);
        }
    }, [found, lessonId, userId]);

    if (!found) {
        return (
            <aside className={cl.panel}>
                <div className={cl.empty}>Урок не найден.</div>
            </aside>
        );
    }

    const { course, lesson } = found;
    const number = lessonIndex(course, lessonId) + 1;
    const doneSteps = new Set(progress.steps);

    /* Следующий урок курса — если он есть. */
    const nextLesson = course.lessons[number] || null;

    /*
     * Переход к следующему уроку. Открываем ЧИСТЫЙ проект (/new): каждый урок
     * строит свою деталь с нуля, и если оставить прежнюю сцену, проверка
     * следующего задания увидит два тела вместо одного.
     */
    const goNext = () => {
        if (!nextLesson) return;
        navigate(`/main/cad-editor/new?lesson=${encodeURIComponent(nextLesson.id)}`);
    };

    return (
        <>
        <aside className={cl.panel}>
            {/* Шапка: где мы в курсе */}
            <header className={cl.head}>
                <div className={cl.crumbs}>
                    <span className={cl.courseName}>{course.title}</span>
                    <span className={cl.lessonNo}>
                        Урок {number} из {course.lessons.length}
                    </span>
                </div>
                <button className={cl.closeBtn} onClick={onClose}
                    title="Закрыть панель урока" type="button">
                    <svg viewBox="0 0 20 20" width="16" height="16">
                        <path d="M5 5l10 10M15 5L5 15" fill="none"
                            stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                    </svg>
                </button>
            </header>

            <h2 className={cl.title}>{lesson.title}</h2>
            <p className={cl.goal}>{lesson.goal}</p>

            {progress.done && (
                <div className={cl.doneBadge}>
                    <CheckIcon done/> Урок пройден
                </div>
            )}

            {/* Пересмотреть мультик. Не вкладка: теория показывается в модалке
                при входе в урок, а сюда вынесен только повтор. */}
            <button type="button" className={cl.replayBtn}
                onClick={() => setTheaterOpen(true)}>
                <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
                    <path d="M4 10a6 6 0 1 1 1.8 4.3" fill="none" stroke="currentColor"
                        strokeWidth="1.7" strokeLinecap="round"/>
                    <path d="M3.4 6.2v3.4h3.4" fill="none" stroke="currentColor"
                        strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Пересмотреть теорию
            </button>

            <div className={cl.scroll}>
                {(
                    <>
                        <section className={cl.block}>
                            <h3 className={cl.blockTitle}>Что нужно сделать</h3>
                            <p className={cl.taskText}>{lesson.task}</p>
                        </section>

                        {/* Чертёж — там, где урок его задал. Для простых задач
                            хватает текста, для деталей посложнее нужны виды. */}
                        {lesson.blueprint && (
                            <section className={cl.block}>
                                <h3 className={cl.blockTitle}>
                                    Чертёж
                                    {/* Панель урока узкая, и размеры на чертеже
                                        выходят мелкими — цифры приходится
                                        разглядывать. Кнопка открывает тот же
                                        чертёж на весь экран. */}
                                    <button
                                        type="button"
                                        className={cl.zoomBtn}
                                        onClick={() => setBlueprintOpen(true)}
                                        title="Открыть чертёж крупно"
                                    >
                                        Увеличить
                                    </button>
                                </h3>
                                <div
                                    className={cl.blueprintClickable}
                                    onClick={() => setBlueprintOpen(true)}
                                    title="Нажми, чтобы открыть крупно"
                                >
                                    <Blueprint blueprint={lesson.blueprint}/>
                                </div>
                            </section>
                        )}

                        <section className={cl.block}>
                            <h3 className={cl.blockTitle}>
                                Шаги
                                <span className={cl.stepCount}>
                                    {doneSteps.size} / {lesson.steps.length}
                                </span>
                            </h3>
                            <ul className={cl.steps}>
                                {lesson.steps.map((step, i) => (
                                    <li key={i}>
                                        <button type="button"
                                            className={doneSteps.has(i) ? cl.stepOn : cl.step}
                                            onClick={() => setProgress(
                                                toggleStep(userId, lessonId, i))}>
                                            <CheckIcon done={doneSteps.has(i)}/>
                                            <span>{step}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </section>

                        {/* Подсказки — по одной, по нажатию */}
                        <section className={cl.block}>
                            <h3 className={cl.blockTitle}>Нужна помощь?</h3>
                            {lesson.hints.slice(0, hintLevel).map((hint, i) => (
                                <p key={i} className={cl.hint}>
                                    <span className={cl.hintNo}>{i + 1}</span>
                                    {hint}
                                </p>
                            ))}
                            {hintLevel < lesson.hints.length && (
                                <button type="button" className={cl.hintBtn}
                                    onClick={() => setHintLevel(hintLevel + 1)}>
                                    {hintLevel === 0 ? 'Показать подсказку'
                                        : 'Ещё подсказку'}
                                </button>
                            )}
                        </section>

                        {/* Результат проверки */}
                        {result && (
                            <section className={result.passed ? cl.verdictOk : cl.verdictBad}>
                                <h3 className={cl.verdictTitle}>
                                    {result.passed ? 'Всё сходится — принято!'
                                        : 'Пока не сходится'}
                                </h3>
                                {result.error && <p className={cl.para}>{result.error}</p>}
                                {/* Что проверка реально видит в документе.
                                    Помогает понять незачёт: операции нет вовсе
                                    или она есть, но названа иначе. */}
                                {!result.passed && result.featureKinds && (
                                    <p className={cl.para}>
                                        Операции в работе:{' '}
                                        {result.featureKinds.length
                                            ? result.featureKinds.join(', ')
                                            : 'ни одной'}
                                    </p>
                                )}
                                <ul className={cl.criteria}>
                                    {result.results.map((r, i) => (
                                        <li key={i} className={cl.criterion}>
                                            <span className={cl.criterionIcon}>
                                                {r.ok ? <CheckIcon done/> : <CrossIcon/>}
                                            </span>
                                            <span className={cl.criterionBody}>
                                                <span className={cl.criterionName}>
                                                    {criterionLabel(r)}
                                                </span>
                                                <span className={cl.criterionNums}>
                                                    у тебя <b>{r.actual}</b>,
                                                    нужно {r.expected}
                                                </span>
                                                {!r.ok && r.message && (
                                                    <span className={cl.criterionMsg}>
                                                        {r.message}
                                                    </span>
                                                )}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                                {lesson.freeform && !result.passed && (
                                    <p className={cl.freeformNote}>
                                        Это свободный проект: проверяются только
                                        общие требования, остальное — на твой вкус.
                                    </p>
                                )}
                            </section>
                        )}
                    </>
                )}
            </div>

            {/* Кнопка проверки — всегда на виду, внизу панели */}
            {/*
             * Подвал. Пока урок не сдан — кнопка проверки. Как только сдан,
             * главной становится «Следующий урок»: висящая «Проверить работу»
             * после зелёной галочки не говорит, что делать дальше.
             * Перепроверить всё равно можно — кнопка остаётся, но неглавной.
             */}
            <footer className={cl.foot}>
                {progress.attempts > 0 && (
                    <span className={cl.attempts}>
                        Попыток: {progress.attempts}
                    </span>
                )}

                {progress.done ? (
                    <>
                        <button type="button" className={cl.recheckBtn}
                            onClick={runCheck} disabled={checking}
                            title="Проверить ещё раз">
                            {checking ? '…' : 'Проверить'}
                        </button>
                        {nextLesson ? (
                            <button type="button" className={cl.checkBtn}
                                onClick={goNext}>
                                Следующий урок
                            </button>
                        ) : (
                            <button type="button" className={cl.checkBtn}
                                onClick={() => navigate('/main/cad-courses')}>
                                Курс пройден — к списку
                            </button>
                        )}
                    </>
                ) : (
                    <button type="button" className={cl.checkBtn}
                        onClick={runCheck} disabled={checking}>
                        {checking ? 'Измеряю деталь…' : 'Проверить работу'}
                    </button>
                )}
            </footer>
        </aside>

        {/* Мультик-теория. Стоит СНАРУЖИ <aside>: панель узкая и со своим
            скроллом, внутри неё модалку обрезало бы по ширине колонки. */}
        {theaterOpen && (
            <TheaterModal
                key={lessonId}
                lesson={lesson}
                onFinish={() => setTheaterOpen(false)}
            />
        )}

        {/* Чертёж крупно. Тоже СНАРУЖИ <aside> — по той же причине, что и
            мультик: внутри узкой колонки его обрезало бы по ширине. */}
        {blueprintOpen && lesson.blueprint && (
            <BlueprintModal
                blueprint={lesson.blueprint}
                title={lesson.title}
                onClose={() => setBlueprintOpen(false)}
            />
        )}
        </>
    );
};


/*
 * Чертёж на весь экран.
 *
 * SVG масштабируется через viewBox, поэтому увеличение не «мылит» линии:
 * размеры и подписи становятся крупнее вместе с деталью — ровно то, чего
 * не хватало в узкой панели урока.
 */
const BlueprintModal = ({ blueprint, title, onClose }) => {
    // Esc закрывает — привычно и не требует целиться в крестик.
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose]);

    return (
        <div
            className={cl.bpBackdrop}
            role="dialog"
            aria-modal="true"
            aria-label={`Чертёж: ${title}`}
            onClick={onClose}                    /* клик по фону закрывает */
        >
            {/* Клик по самому чертежу не должен закрывать окно. */}
            <div className={cl.bpModal} onClick={(e) => e.stopPropagation()}>
                <header className={cl.bpHead}>
                    <span className={cl.bpTitle}>Чертёж — {title}</span>
                    <button
                        type="button"
                        className={cl.bpClose}
                        onClick={onClose}
                        title="Закрыть (Esc)"
                    >
                        ✕
                    </button>
                </header>
                {/* Примечание под чертежом Blueprint выводит сам — здесь его
                    дублировать не нужно. */}
                <div className={cl.bpBody}>
                    <Blueprint blueprint={blueprint}/>
                </div>
            </div>
        </div>
    );
};

export default LessonPanel;
